export interface MIImportOptions {
  format?: 'auto' | 'geojson' | 'kml' | 'kmz' | 'gml' | 'shapefile' | 'gpx' | 'csv' |
           'geotiff' | 'dted' | 'nitf' | 'mbtiles' | 'geopackage' | 'wkt' | 'topojson' |
           'citygml' | 'las' | 'laz';
  projection?: string;
  targetProjection?: string;
  layerName?: string;
  style?: any;
}

export interface MIExportOptions {
  format: 'geojson' | 'kml' | 'shapefile' | 'gml' | 'gpx' | 'csv' | 'geotiff' |
          'geopackage' | 'wkt' | 'citygml' | 'las' | 'laz' | '3dtiles';
  projection?: string;
  includeStyles?: boolean;
  filename?: string;
  extent?: any;
  selectedFeaturesOnly?: boolean;
}

export type SupportedImportFormat = NonNullable<MIImportOptions['format']>;
export type SupportedExportFormat = MIExportOptions['format'];

const FORMAT_EXTENSIONS: Record<string, SupportedImportFormat> = {
  '.geojson': 'geojson',
  '.json': 'geojson',
  '.kml': 'kml',
  '.kmz': 'kmz',
  '.gml': 'gml',
  '.shp': 'shapefile',
  '.gpx': 'gpx',
  '.csv': 'csv',
  '.tif': 'geotiff',
  '.tiff': 'geotiff',
  '.dt0': 'dted',
  '.dt1': 'dted',
  '.dt2': 'dted',
  '.ntf': 'nitf',
  '.mbtiles': 'mbtiles',
  '.gpkg': 'geopackage',
  '.wkt': 'wkt',
  '.topojson': 'topojson',
  '.citygml': 'citygml',
  '.las': 'las',
  '.laz': 'laz',
};

const FORMAT_MIME_TYPES: Record<string, string> = {
  geojson: 'application/geo+json',
  kml: 'application/vnd.google-earth.kml+xml',
  gpx: 'application/gpx+xml',
  gml: 'application/gml+xml',
  csv: 'text/csv',
  wkt: 'text/plain',
  shapefile: 'application/x-shapefile',
  geotiff: 'image/tiff',
  citygml: 'application/gml+xml',
};

export class MIDataExchange {
  static detectFormat(source: string | File): SupportedImportFormat {
    let filename = '';
    if (typeof source === 'string') {
      filename = source.toLowerCase();
    } else if (source instanceof File) {
      filename = source.name.toLowerCase();
    }

    for (const [ext, format] of Object.entries(FORMAT_EXTENSIONS)) {
      if (filename.endsWith(ext)) return format;
    }
    return 'geojson';
  }

  static async import(source: string | File | Blob, options?: MIImportOptions): Promise<any> {
    const format = options?.format === 'auto' || !options?.format
      ? (typeof source === 'string' || source instanceof File ? this.detectFormat(source as any) : 'geojson')
      : options.format;

    let text = '';
    if (typeof source === 'string') {
      if (source.startsWith('http') || source.startsWith('//')) {
        const response = await fetch(source);
        text = await response.text();
      } else {
        text = source;
      }
    } else {
      text = await (source as Blob).text();
    }

    switch (format) {
      case 'geojson':
        return this.parseGeoJSON(text, options);
      case 'kml':
        return this.parseKML(text, options);
      case 'gpx':
        return this.parseGPX(text, options);
      case 'csv':
        return this.parseCSV(text, options);
      case 'wkt':
        return this.parseWKT(text, options);
      case 'topojson':
        return this.parseTopoJSON(text, options);
      case 'gml':
      case 'citygml':
        return this.parseGML(text, options);
      default:
        return this.parseGeoJSON(text, options);
    }
  }

  static async export(layers: any[], options: MIExportOptions): Promise<Blob> {
    const features: GeoJSON.Feature[] = [];
    // A native source may have several renderer layers (for example a fill
    // and outline). Adoption adapters expose this stable identity so export
    // includes source data once without hiding those renderer layers from
    // layer-management UI.
    const exportedSourceIds = new Set<string>();
    for (const layer of layers) {
      // SDK GeoJsonSource exposes `data`/`setData`, not getData().  3D
      // GeoJsonDataSource3D similarly owns data directly. Preserve Feature
      // and bare Geometry input instead of silently exporting an empty file.
      const source = layer?.getSource?.();
      const sourceId = layer?.exportSourceId ?? source?.exportSourceId;
      if (typeof sourceId === 'string') {
        if (exportedSourceIds.has(sourceId)) continue;
        exportedSourceIds.add(sourceId);
      }
      const data = source?.data ?? layer?.data;
      if (!data) continue;
      if (data.type === 'FeatureCollection' && Array.isArray(data.features)) {
        features.push(...data.features);
      } else if (data.type === 'Feature') {
        features.push(data);
      } else if (typeof data.type === 'string' && data.coordinates) {
        features.push({ type: 'Feature', properties: {}, geometry: data } as GeoJSON.Feature);
      }
    }

    const collection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features
    };

    switch (options.format) {
      case 'geojson':
        return new Blob([JSON.stringify(collection, null, 2)], { type: 'application/geo+json' });
      case 'kml':
        return new Blob([this.toKML(collection)], { type: 'application/vnd.google-earth.kml+xml' });
      case 'gpx':
        return new Blob([this.toGPX(collection)], { type: 'application/gpx+xml' });
      case 'csv':
        return new Blob([this.toCSV(collection)], { type: 'text/csv' });
      case 'wkt':
        return new Blob([this.toWKT(collection)], { type: 'text/plain' });
      default:
        throw new Error(
          `Export format "${options.format}" is advertised by the type contract but is not implemented by MIDataExchange.export(). ` +
          'Use geojson, kml, gpx, csv, or wkt.'
        );
    }
  }

  static getSupportedImportFormats(): { format: SupportedImportFormat; label: string; extensions: string[] }[] {
    return [
      { format: 'geojson', label: 'GeoJSON', extensions: ['.geojson', '.json'] },
      { format: 'kml', label: 'KML', extensions: ['.kml'] },
      { format: 'kmz', label: 'KMZ', extensions: ['.kmz'] },
      { format: 'gpx', label: 'GPX', extensions: ['.gpx'] },
      { format: 'gml', label: 'GML', extensions: ['.gml'] },
      { format: 'csv', label: 'CSV', extensions: ['.csv'] },
      { format: 'wkt', label: 'WKT', extensions: ['.wkt'] },
      { format: 'topojson', label: 'TopoJSON', extensions: ['.topojson'] },
      { format: 'shapefile', label: 'Shapefile', extensions: ['.shp'] },
      { format: 'geotiff', label: 'GeoTIFF', extensions: ['.tif', '.tiff'] },
      { format: 'geopackage', label: 'GeoPackage', extensions: ['.gpkg'] },
      { format: 'dted', label: 'DTED', extensions: ['.dt0', '.dt1', '.dt2'] },
      { format: 'citygml', label: 'CityGML', extensions: ['.citygml'] },
      { format: 'las', label: 'LAS Point Cloud', extensions: ['.las'] },
      { format: 'laz', label: 'LAZ Point Cloud', extensions: ['.laz'] },
    ];
  }

  static getSupportedExportFormats(): { format: SupportedExportFormat; label: string }[] {
    return [
      { format: 'geojson', label: 'GeoJSON' },
      { format: 'kml', label: 'KML' },
      { format: 'gpx', label: 'GPX' },
      { format: 'gml', label: 'GML' },
      { format: 'csv', label: 'CSV' },
      { format: 'wkt', label: 'WKT' },
      { format: 'shapefile', label: 'Shapefile' },
      { format: 'geotiff', label: 'GeoTIFF' },
      { format: 'geopackage', label: 'GeoPackage' },
      { format: 'citygml', label: 'CityGML' },
      { format: 'las', label: 'LAS Point Cloud' },
      { format: 'laz', label: 'LAZ Point Cloud' },
      { format: '3dtiles', label: '3D Tiles' },
    ];
  }

  private static parseGeoJSON(text: string, _options?: MIImportOptions): GeoJSON.FeatureCollection {
    try {
      const parsed = JSON.parse(text);
      if (parsed.type === 'FeatureCollection') return parsed;
      if (parsed.type === 'Feature') return { type: 'FeatureCollection', features: [parsed] };
      return { type: 'FeatureCollection', features: [{ type: 'Feature', geometry: parsed, properties: {} }] };
    } catch {
      return { type: 'FeatureCollection', features: [] };
    }
  }

  private static parseKML(text: string, _options?: MIImportOptions): GeoJSON.FeatureCollection {
    const features: GeoJSON.Feature[] = [];
    const placemarkRegex = /<Placemark>([\s\S]*?)<\/Placemark>/gi;
    const nameRegex = /<name>(.*?)<\/name>/i;
    const descRegex = /<description>(.*?)<\/description>/i;
    const coordRegex = /<coordinates>([\s\S]*?)<\/coordinates>/gi;
    const pointRegex = /<Point>/i;
    const lineRegex = /<LineString>/i;
    const polygonRegex = /<Polygon>/i;
    let match;
    while ((match = placemarkRegex.exec(text)) !== null) {
      const pm = match[1];
      const name = nameRegex.exec(pm)?.[1] || '';
      const desc = descRegex.exec(pm)?.[1] || '';
      const properties: Record<string, string> = { name, description: desc };
      let coordMatch;
      const allCoords: number[][] = [];
      const coordRegexLocal = /<coordinates>([\s\S]*?)<\/coordinates>/gi;
      while ((coordMatch = coordRegexLocal.exec(pm)) !== null) {
        const coordParts = coordMatch[1].trim().split(/\s+/);
        for (const part of coordParts) {
          const [lon, lat, alt] = part.split(',').map(Number);
          if (!isNaN(lon) && !isNaN(lat)) allCoords.push(alt ? [lon, lat, alt] : [lon, lat]);
        }
      }
      if (allCoords.length === 0) continue;
      let geometry: GeoJSON.Geometry;
      if (pointRegex.test(pm) && allCoords.length === 1) {
        geometry = { type: 'Point', coordinates: allCoords[0] };
      } else if (polygonRegex.test(pm)) {
        geometry = { type: 'Polygon', coordinates: [allCoords] };
      } else if (lineRegex.test(pm) || allCoords.length > 1) {
        geometry = { type: 'LineString', coordinates: allCoords };
      } else {
        geometry = { type: 'Point', coordinates: allCoords[0] };
      }
      features.push({ type: 'Feature', properties, geometry });
    }
    return { type: 'FeatureCollection', features };
  }

  private static parseGPX(text: string, _options?: MIImportOptions): GeoJSON.FeatureCollection {
    const features: GeoJSON.Feature[] = [];
    const wptRegex = /<wpt\s+lat="([\d.-]+)"\s+lon="([\d.-]+)"[^>]*>([\s\S]*?)<\/wpt>/gi;
    const trkRegex = /<trkseg>([\s\S]*?)<\/trkseg>/gi;
    const trkptRegex = /<trkpt\s+lat="([\d.-]+)"\s+lon="([\d.-]+)"/gi;
    const nameRegex = /<name>(.*?)<\/name>/i;
    let match;
    while ((match = wptRegex.exec(text)) !== null) {
      const lat = parseFloat(match[1]);
      const lon = parseFloat(match[2]);
      const name = nameRegex.exec(match[3])?.[1] || '';
      features.push({ type: 'Feature', properties: { name }, geometry: { type: 'Point', coordinates: [lon, lat] } });
    }
    while ((match = trkRegex.exec(text)) !== null) {
      const coords: number[][] = [];
      let ptMatch;
      while ((ptMatch = trkptRegex.exec(match[1])) !== null) {
        coords.push([parseFloat(ptMatch[2]), parseFloat(ptMatch[1])]);
      }
      if (coords.length > 0) {
        features.push({ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: coords } });
      }
    }
    return { type: 'FeatureCollection', features };
  }

  private static parseCSV(text: string, _options?: MIImportOptions): GeoJSON.FeatureCollection {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return { type: 'FeatureCollection', features: [] };
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const latIdx = headers.findIndex(h => ['lat', 'latitude', 'y'].includes(h));
    const lonIdx = headers.findIndex(h => ['lon', 'lng', 'longitude', 'x'].includes(h));
    if (latIdx < 0 || lonIdx < 0) return { type: 'FeatureCollection', features: [] };
    const features: GeoJSON.Feature[] = [];
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const lat = parseFloat(values[latIdx]);
      const lon = parseFloat(values[lonIdx]);
      if (isNaN(lat) || isNaN(lon)) continue;
      const properties: Record<string, string> = {};
      headers.forEach((h, idx) => {
        if (idx !== latIdx && idx !== lonIdx) properties[h] = values[idx] || '';
      });
      features.push({ type: 'Feature', properties, geometry: { type: 'Point', coordinates: [lon, lat] } });
    }
    return { type: 'FeatureCollection', features };
  }

  private static parseWKT(text: string, _options?: MIImportOptions): GeoJSON.FeatureCollection {
    const features: GeoJSON.Feature[] = [];
    const wktLines = text.trim().split('\n');
    for (const line of wktLines) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      const geometry = this.wktToGeoJSON(trimmed);
      if (geometry) {
        features.push({ type: 'Feature', properties: {}, geometry });
      }
    }
    return { type: 'FeatureCollection', features };
  }

  private static wktToGeoJSON(wkt: string): GeoJSON.Geometry | null {
    const pointMatch = /^POINT\s*\(\s*([\d.-]+)\s+([\d.-]+)\s*\)$/i.exec(wkt);
    if (pointMatch) return { type: 'Point', coordinates: [parseFloat(pointMatch[1]), parseFloat(pointMatch[2])] };
    const lineMatch = /^LINESTRING\s*\((.*)\)$/i.exec(wkt);
    if (lineMatch) {
      const coords = lineMatch[1].split(',').map(c => {
        const [x, y] = c.trim().split(/\s+/).map(Number);
        return [x, y];
      });
      return { type: 'LineString', coordinates: coords };
    }
    return null;
  }

  private static parseTopoJSON(text: string, _options?: MIImportOptions): GeoJSON.FeatureCollection {
    try {
      const topo = JSON.parse(text);
      const features: GeoJSON.Feature[] = [];
      if (topo.objects) {
        for (const key of Object.keys(topo.objects)) {
          const obj = topo.objects[key];
          if (obj.geometries) {
            for (const geom of obj.geometries) {
              features.push({ type: 'Feature', properties: geom.properties || {}, geometry: geom });
            }
          }
        }
      }
      return { type: 'FeatureCollection', features };
    } catch {
      return { type: 'FeatureCollection', features: [] };
    }
  }

  private static parseGML(text: string, _options?: MIImportOptions): GeoJSON.FeatureCollection {
    const features: GeoJSON.Feature[] = [];
    const memberRegex = /<(?:gml:)?featureMember>([\s\S]*?)<\/(?:gml:)?featureMember>/gi;
    const posRegex = /<(?:gml:)?pos>([\s\S]*?)<\/(?:gml:)?pos>/gi;
    let match;
    while ((match = memberRegex.exec(text)) !== null) {
      const member = match[1];
      let posMatch;
      while ((posMatch = posRegex.exec(member)) !== null) {
        const [lat, lon] = posMatch[1].trim().split(/\s+/).map(Number);
        if (!isNaN(lat) && !isNaN(lon)) {
          features.push({ type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [lon, lat] } });
        }
      }
    }
    return { type: 'FeatureCollection', features };
  }

  private static toKML(collection: GeoJSON.FeatureCollection): string {
    let kml = '<?xml version="1.0" encoding="UTF-8"?>\n<kml xmlns="http://www.opengis.net/kml/2.2">\n<Document>\n';
    for (const feature of collection.features) {
      kml += '<Placemark>\n';
      if (feature.properties?.name) kml += `  <name>${feature.properties.name}</name>\n`;
      if (feature.properties?.description) kml += `  <description>${feature.properties.description}</description>\n`;
      if (feature.geometry.type === 'Point') {
        const [lon, lat, alt] = feature.geometry.coordinates;
        kml += `  <Point><coordinates>${lon},${lat}${alt ? ',' + alt : ''}</coordinates></Point>\n`;
      } else if (feature.geometry.type === 'LineString') {
        kml += '  <LineString><coordinates>';
        kml += feature.geometry.coordinates.map(c => c.join(',')).join(' ');
        kml += '</coordinates></LineString>\n';
      } else if (feature.geometry.type === 'Polygon') {
        kml += '  <Polygon><outerBoundaryIs><LinearRing><coordinates>';
        kml += feature.geometry.coordinates[0].map(c => c.join(',')).join(' ');
        kml += '</coordinates></LinearRing></outerBoundaryIs></Polygon>\n';
      }
      kml += '</Placemark>\n';
    }
    kml += '</Document>\n</kml>';
    return kml;
  }

  private static toGPX(collection: GeoJSON.FeatureCollection): string {
    let gpx = '<?xml version="1.0" encoding="UTF-8"?>\n<gpx version="1.1" xmlns="http://www.topografix.com/GPX/1/1">\n';
    for (const feature of collection.features) {
      if (feature.geometry.type === 'Point') {
        const [lon, lat] = feature.geometry.coordinates;
        gpx += `<wpt lat="${lat}" lon="${lon}">`;
        if (feature.properties?.name) gpx += `<name>${feature.properties.name}</name>`;
        gpx += '</wpt>\n';
      } else if (feature.geometry.type === 'LineString') {
        gpx += '<trk><trkseg>';
        for (const coord of feature.geometry.coordinates) {
          gpx += `<trkpt lat="${coord[1]}" lon="${coord[0]}"/>`;
        }
        gpx += '</trkseg></trk>\n';
      }
    }
    gpx += '</gpx>';
    return gpx;
  }

  private static toCSV(collection: GeoJSON.FeatureCollection): string {
    const allKeys = new Set<string>();
    collection.features.forEach(f => {
      if (f.properties) Object.keys(f.properties).forEach(k => allKeys.add(k));
    });
    const keys = Array.from(allKeys);
    let csv = `latitude,longitude,${keys.join(',')}\n`;
    for (const feature of collection.features) {
      if (feature.geometry.type === 'Point') {
        const [lon, lat] = feature.geometry.coordinates;
        const vals = keys.map(k => feature.properties?.[k] ?? '');
        csv += `${lat},${lon},${vals.join(',')}\n`;
      }
    }
    return csv;
  }

  private static toWKT(collection: GeoJSON.FeatureCollection): string {
    return collection.features.map(f => {
      const geom = f.geometry;
      if (geom.type === 'Point') return `POINT (${geom.coordinates[0]} ${geom.coordinates[1]})`;
      if (geom.type === 'LineString') return `LINESTRING (${geom.coordinates.map(c => c.join(' ')).join(', ')})`;
      if (geom.type === 'Polygon') return `POLYGON ((${geom.coordinates[0].map(c => c.join(' ')).join(', ')}))`;
      return '';
    }).filter(Boolean).join('\n');
  }
}
